/* eslint-disable sonarjs/cognitive-complexity */
import Link from 'next/link';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { BILLING_ADDRESS } from '../../brands/default/content/components/address';
import {
  AUTO_ADDRESS_LOOKUP,
  EIR_POSTCODE,
  TEXT_LOWERCASE,
} from '../../brands/default/features';
import { selectAddressByMoniker, findAddress } from '../../helpers/address';
import { trackEvent } from '../../helpers/analytics';
import ThemeContext from '../../providers/ThemeProvider';
import { setLoadingState } from '../../redux/actions/loading';
import { LOADING_STATUS } from '../../redux/reducers/loading';
import Message from '../_generic/Message';
import FormikInput from '../_generic/input/FormikInput';
import useToast from '../_generic/toast/UseToast';
import AddressFields from './AddressFields';
import ListOfAddresses from './ListOfAddresses';
import LookupAddress from './LookupAddress';

const BillingAddress = ({
  disabled = false,
  errors = {},
  extraClasses = '',
  isRegistrationPage = false,
  isShowError = false,
  lookupAddressText = '',
  onBlurFunction,
  onChangeFunction,
  setValues,
  showAllFields = false,
  touched = {},
  values = {},
}) => {
  const theme = useContext(ThemeContext);
  const [showAddressFields, setShowAddressFields] = useState(showAllFields);
  const [fetchedAddresses, setFetchedAddresses] = useState();
  const toast = useToast();
  const dispatch = useDispatch();

  const {
    enterAddressManually,
    lookupOrEnterAddress,
    registerPostcodeDoesntExist,
    registrationEirCode,
    registrationLookupError,
  } = BILLING_ADDRESS;

  const classes = `mobile-highlight-border ${extraClasses} ${
    TEXT_LOWERCASE && 'lowercase_placeholder'
  }`;

  useEffect(() => {
    if (!AUTO_ADDRESS_LOOKUP) {
      setShowAddressFields(true);
    }
  }, []);

  const handleBlur = (event) => {
    const name = event.target.name;
    if (name === 'addressLine1') trackEvent('address_line1_field');
    if (name === 'city') trackEvent('address_city_field');
    if (name === 'addressLine2') trackEvent('address_line2_field');

    if (name === 'postCode') {
      if (EIR_POSTCODE) {
        trackEvent('eircode_field');
      } else {
        trackEvent('postcode_field');
      }
    }

    onBlurFunction(event);
  };

  const manualAddress = (event) => {
    // keyboard - only trigger with enter key
    if (event && event.key && event.key !== 'Enter') return;

    event.preventDefault();
    setShowAddressFields(true);
  };

  const lookupAddress = async () => {
    dispatch(setLoadingState(LOADING_STATUS.LOADING));
    const response = await findAddress(values.postCode);
    dispatch(setLoadingState(LOADING_STATUS.READY));

    if (!response || response.status !== (200 || 202)) {
      setFetchedAddresses(registerPostcodeDoesntExist);
      setShowAddressFields(true);

      return;
    }

    const addressReferences =
      response.data.data.apiAddressReferencesVO.addressReferences;

    if (addressReferences.length > 0) {
      setFetchedAddresses(addressReferences);
    } else {
      setFetchedAddresses(registerPostcodeDoesntExist);
    }
  };

  const selectAddress = async (moniker) => {
    dispatch(setLoadingState(LOADING_STATUS.LOADING));
    const response = await selectAddressByMoniker(moniker);
    dispatch(setLoadingState(LOADING_STATUS.READY));

    if (!response || response.status !== (200 || 202)) {
      toast.addToast(registrationLookupError, 'toast-error');
      setFetchedAddresses();
      setShowAddressFields(true);

      return;
    }

    const selectedAddress = response.data.data.apiAddressVO;

    if (selectedAddress) {
      setValues({
        ...values,
        addressLine1: selectedAddress.addressLine1,
        addressLine2: selectedAddress.addressLine2,
        city: selectedAddress.town,
        postCode: selectedAddress.postcode,
      });
    } else {
      toast.addToast(registrationLookupError, 'toast-error');
    }
    setFetchedAddresses();
    setShowAddressFields(true);
  };

  return (
    <>
      {AUTO_ADDRESS_LOOKUP && (
        <LookupAddress
          disabled={disabled}
          errors={errors}
          extraClasses={classes}
          lookupAddress={lookupAddress}
          lookupAddressText={lookupAddressText}
          onBlurFunction={handleBlur}
          onChangeFunction={onChangeFunction}
          touched={touched}
          values={values}
        />
      )}
      {Boolean(fetchedAddresses) && (
        <div className="col-12">
          <ListOfAddresses
            addressList={fetchedAddresses}
            selectAddress={selectAddress}
          />
        </div>
      )}
      {isShowError && !showAddressFields && (
        <div className="col-12">
          <Message
            message={lookupOrEnterAddress}
            showIcon={false}
            type="ERROR"
          />
        </div>
      )}
      {AUTO_ADDRESS_LOOKUP && !showAddressFields && (
        <div className="col-12 input-container text-right">
          <Link href="#">
            <a
              className="manual-address"
              onClick={manualAddress}
              onKeyDown={manualAddress}
              role="link"
              tabIndex={0}
            >
              {enterAddressManually}
            </a>
          </Link>
        </div>
      )}
      {showAddressFields && (
        <AddressFields
          componentClass={classes}
          disabled={disabled}
          errors={errors}
          onBlurFunction={handleBlur}
          onChangeFunction={onChangeFunction}
          touched={touched}
          values={values}
        />
      )}
      {!AUTO_ADDRESS_LOOKUP && showAddressFields && (
        <div className="col-12">
          <FormikInput
            disabled={disabled}
            extraClasses={classes}
            inputTouched={touched.postCode}
            inputValue={values.postCode}
            name={'postCode'}
            onBlurFunction={handleBlur}
            onChangeFunction={onChangeFunction}
            placeholder={`${registrationEirCode}${
              isRegistrationPage ? '' : '*'
            }`}
            required={!isRegistrationPage}
            type="text"
          />
        </div>
      )}
      <style jsx>{`
        a.manual-address {
          color: ${theme.colors.primary};
        }
      `}</style>
    </>
  );
};

BillingAddress.propTypes = {
  disabled: PropTypes.bool,
  errors: PropTypes.object.isRequired,
  extraClasses: PropTypes.string,
  isRegistrationPage: PropTypes.bool,
  isShowError: PropTypes.bool,
  lookupAddressText: PropTypes.string,
  onBlurFunction: PropTypes.func.isRequired,
  onChangeFunction: PropTypes.func.isRequired,
  setValues: PropTypes.func.isRequired,
  showAllFields: PropTypes.bool,
  touched: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
};

export default BillingAddress;
